import React, { useEffect, useState } from 'react';
import { Container, Box, CircularProgress, Typography, Button, Paper, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import RegistrationStatusAlert from '../../components/RegistrationStatusAlert';
import NgoRegistrationClient from '../api/NgoRegistrationClient';
import { APP_ROUTES } from '../constants/routes';
import RecifeBemLogo from "~/images/illustrations/bora-impactar.svg";
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import { useAuth } from '../hooks/useAuth';

export default function RegistrationStatusPage() {
  const [registrationData, setRegistrationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { signOut } = useAuth();

  useEffect(() => {
    const fetchRegistrationStatus = async () => {
      try {
        const response = await NgoRegistrationClient.getRegistrationStatus();
        
        if (response?.status) {
          const data = {
            status: response.status,
            rejectionReason: response.reason_for_rejection,
            observations: response.observations,
            ngo: {
              name: response.name,
              description: response.description,
              start_year: response.start_year,
              email: response.email,
              phone: response.phone,
              instagram: response.instagram,
              facebook: response.facebook,
              x: response.x,
              site: response.site,
              cnpj: response.cnpj,
              type: response.type,
              ods: response.ods
            },
            user: {
              user_name: response.user_name,
              user_email: response.user_email
            }
          };
          
          setRegistrationData(data);
        } else {
          setError('Nenhuma solicitação de cadastro encontrada para este usuário.');
        }
      } catch (error) {
        if (error.response?.status === 401) {
          setError('Você precisa estar logado para ver o status do seu cadastro.');
        } else if (error.response?.status === 404) {
          setError('Nenhuma solicitação de cadastro encontrada para este usuário.');
        } else {
          setError('Ocorreu um erro ao buscar o status do cadastro. Por favor, tente novamente mais tarde.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRegistrationStatus();
  }, []);

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '200px'
        }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          textAlign: 'center'
        }}>
          <Typography variant="h6" color="error">
            {error}
          </Typography>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => navigate(APP_ROUTES.SIGN_IN)}
            sx={{
              py: { xs: 1.25, sm: 1.5 },
              px: { xs: 3, sm: 4 },
              fontSize: { xs: '1rem', sm: '1.1rem' },
              fontWeight: 600,
              borderRadius: { xs: 1.5, sm: 2 },
              background: 'linear-gradient(90deg, #294BB6 0%, #4F97FF 100%)',
              transition: 'all 0.3s ease',
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(90deg, #4F97FF 0%, #294BB6 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(41, 75, 182, 0.25)'
              },
              '&:active': {
                transform: 'translateY(0)'
              }
            }}
          >
            Fazer Login
          </Button>
        </Box>
      );
    }

    return (
      <RegistrationStatusAlert 
        status={registrationData.status}
        rejectionReason={registrationData.rejectionReason}
        observations={registrationData.observations}
        registrationData={registrationData}
      />
    );
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#CBEFFF',
        position: 'relative',
        pt: { xs: 8, sm: 8, md: 10 },
        pb: { xs: 4, sm: 6, md: 8 },
        px: { xs: 2, sm: 3, md: 4 }
      }}
    >
      <Container 
        maxWidth="lg" 
        sx={{ 
          p: 0,
          height: '100%',
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' }
        }}
      >
        <Paper 
          elevation={3} 
          sx={{ 
            p: { xs: 2.5, sm: 3, md: 3.5 }, 
            borderRadius: { xs: 2, sm: 3 },
            bgcolor: 'white',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #294BB6 0%, #4F97FF 100%)'
            }
          }}
        >
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%'
          }}>
            <Stack 
              direction="row" 
              spacing={2} 
              sx={{ 
                width: '100%', 
                justifyContent: 'flex-end',
                mb: 2 
              }}
            >
              <Button
                variant="outlined"
                startIcon={<HomeIcon />}
                onClick={() => navigate(APP_ROUTES.ROOT)}
                sx={{
                  borderColor: '#294BB6',
                  color: '#294BB6',
                  '&:hover': {
                    borderColor: '#4F97FF',
                    backgroundColor: 'rgba(79, 151, 255, 0.04)'
                  }
                }}
              >
                Página Inicial
              </Button>
              <Button
                variant="outlined"
                startIcon={<LogoutIcon />}
                onClick={async () => {
                  await signOut();
                  window.location.href = '/';
                }}
                sx={{
                  borderColor: '#d32f2f',
                  color: '#d32f2f',
                  '&:hover': {
                    borderColor: '#f44336',
                    backgroundColor: 'rgba(244, 67, 54, 0.04)'
                  }
                }}
              >
                Sair
              </Button>
            </Stack>
            <img 
              src={RecifeBemLogo} 
              alt="Bora Impactar" 
              style={{
                width: '160px',
                maxWidth: '100%',
                height: 'auto',
                marginBottom: '20px'
              }}
            />
            {renderContent()}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
} 